import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import useMasterData from '../../hooks/useMasterData';
import FormInput from '../../components/FormInput';
import FormSelect from '../../components/FormSelect';
import FormFileInput from '../../components/FormFileInput';
import FormCheckboxGroup from '../../components/FormCheckboxGroup';
import { supabase } from '../../services/supabaseClient';
import { useAuth } from '../../contexts/AuthContext';
import { BADAN_HUKUM_OPTIONS, pdfRules, uploadRegistrationDocument } from '../../utils/registrationDocuments';

const PendaftaranBaru = () => {
    const navigate = useNavigate();
    const { refreshMemberStatus } = useAuth();
    const [submitError, setSubmitError] = useState('');

    const { register, handleSubmit, watch, formState: { errors, isSubmitting } } = useForm({
        mode: 'onChange',
        defaultValues: { bidangUsaha: [] }
    });

    const {
        provinces,
        businessTypes,
        cities,
        isLoading,
        fetchCities
    } = useMasterData({ activeBusinessTypesOnly: true });

    const selectedProvince = watch('provinsi');
    const badanHukum = watch('badanHukum');
    const bidangUsaha = watch('bidangUsaha');

    const isYayasan = badanHukum === 'Yayasan';
    const lainnyaId = businessTypes.find((type) => type.name.toLowerCase() === 'lainnya')?.id;
    const lainnyaDipilih = Boolean(lainnyaId) && Array.isArray(bidangUsaha) && bidangUsaha.includes(lainnyaId);

    useEffect(() => {
        if (selectedProvince) {
            fetchCities(selectedProvince);
        }
    }, [selectedProvince, fetchCities]);

    const generateTrackingCode = () => {
        return Math.random().toString(36).substring(2, 10).toUpperCase();
    };

    const cleanText = (value) => {
        const text = (value || '').trim();
        return text === '' ? null : text;
    };

    const onSubmit = async (data) => {
        setSubmitError('');
        try {
            let authUser = null;

            // 1. Auth SignUp & Idempotency
            const { data: signUpData, error: signUpError } = await supabase.auth.signUp({
                email: data.email,
                password: data.password,
            });

            if (signUpError) {
                // Skenario Error: Jika email sudah terdaftar/gagal signUp, coba signIn untuk melanjutkan proses
                const { data: signInData, error: signInError } = await supabase.auth.signInWithPassword({
                    email: data.email,
                    password: data.password,
                });

                if (signInError) {
                    throw new Error(`Akun tidak dapat dibuat (${signUpError.message}). Jika email ini sudah pernah dipakai mendaftar, masukkan password yang sama dengan sebelumnya.`);
                }
                authUser = signInData.user;
            } else {
                authUser = signUpData.user;
            }

            if (!authUser) {
                throw new Error('Gagal mendapatkan sesi pengguna.');
            }

            // 2. Upload Storage. Logo wajib. Akta/SK dan izin Kemenag opsional.
            const logoFile = data.logo[0];
            const aktaFile = data.aktaSk && data.aktaSk.length > 0 ? data.aktaSk[0] : null;
            const kemenagFile = data.izinKemenag && data.izinKemenag.length > 0 ? data.izinKemenag[0] : null;

            const logoFileName = `${authUser.id}_${Date.now()}_${logoFile.name}`;

            const { error: logoError } = await supabase.storage
                .from('registration_logos')
                .upload(logoFileName, logoFile);

            if (logoError) throw new Error('Gagal mengunggah logo: ' + logoError.message);

            const { data: logoUrlData } = supabase.storage
                .from('registration_logos')
                .getPublicUrl(logoFileName);

            let aktaUrl = null;
            if (aktaFile) {
                try {
                    aktaUrl = await uploadRegistrationDocument(authUser.id, aktaFile, 'akta-sk');
                } catch (uploadError) {
                    throw new Error('Gagal mengunggah akta atau SK: ' + uploadError.message);
                }
            }

            let kemenagUrl = null;
            if (kemenagFile) {
                try {
                    kemenagUrl = await uploadRegistrationDocument(authUser.id, kemenagFile, 'izin-kemenag');
                } catch (uploadError) {
                    throw new Error('Gagal mengunggah izin Kemenag: ' + uploadError.message);
                }
            }

            const trackingCode = generateTrackingCode();

            // 3. Insert Database & Data Mapping. Semua bidang usaha dikirim sekaligus lewat
            // business_type_ids; database mengisi business_type_id dan tabel relasinya.
            const { error: insertError } = await supabase
                .from('registrations')
                .insert([{
                    user_id: authUser.id,
                    email: data.email,
                    pesantren_name: data.namaPesantren,
                    leader_name: data.pimpinan,
                    pic_hebitren_name: data.penanggungJawab,
                    contact_number: data.nomorKontak,
                    province_id: data.provinsi,
                    city_id: data.kota,
                    business_type_ids: data.bidangUsaha,
                    other_business_text: lainnyaDipilih ? cleanText(data.bidangLainnya) : null,
                    legal_entity_type: data.badanHukum || null,
                    deed_number: isYayasan ? cleanText(data.nomorAkta) : null,
                    notary_name: isYayasan ? cleanText(data.notaris) : null,
                    sk_number: isYayasan ? cleanText(data.nomorSk) : null,
                    logo_url: logoUrlData.publicUrl,
                    document_url: aktaUrl,
                    kemenag_document_url: kemenagUrl,
                    status: 'Menunggu verifikasi',
                    tracking_code: trackingCode
                }]);

            if (insertError) {
                throw new Error('Gagal menyimpan data pendaftaran ke database: ' + insertError.message);
            }

            // Muat ulang status akun, lalu pindah ke layar Menunggu Verifikasi
            await refreshMemberStatus();
            navigate('/member', { replace: true });

        } catch (error) {
            console.error('Submission Error:', error);
            // Jangan hapus user, biarkan idempotency bekerja jika disubmit ulang
            setSubmitError(error.message);
        }
    };

    return (
        <div className="min-h-screen bg-gray-50 py-10 px-4 sm:px-6 lg:px-8 flex justify-center">
            <div className="max-w-3xl w-full bg-white p-8 shadow-md rounded-lg border border-gray-200">
                <div className="mb-8 border-b pb-4">
                    <h2 className="text-2xl font-bold text-gray-800">Formulir Pendaftaran Baru</h2>
                    <p className="text-sm text-gray-500 mt-1">Lengkapi data di bawah ini untuk mendaftar sebagai anggota HEBITREN.</p>
                </div>

                <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
                    {/* Section: Identitas & Penanggung Jawab */}
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <FormInput
                            label="Identitas (Nama Pesantren)"
                            name="namaPesantren"
                            register={register}
                            errors={errors}
                            placeholder="Contoh: Pondok Pesantren Al-Baqarah"
                            rules={{ required: 'Nama Pesantren wajib diisi' }}
                        />
                        <FormInput
                            label="Pimpinan Pesantren"
                            name="pimpinan"
                            register={register}
                            errors={errors}
                            placeholder="Nama Lengkap Pimpinan"
                            rules={{ required: 'Nama pimpinan wajib diisi' }}
                        />
                        <FormInput
                            label="Penanggung Jawab Pesantren"
                            name="penanggungJawab"
                            register={register}
                            errors={errors}
                            placeholder="Nama Penanggung Jawab"
                            rules={{ required: 'Penanggung jawab wajib diisi' }}
                        />
                        <FormInput
                            label="Nomor Kontak"
                            name="nomorKontak"
                            type="tel"
                            register={register}
                            errors={errors}
                            placeholder="08123456789"
                            rules={{
                                required: 'Nomor kontak wajib diisi',
                                pattern: {
                                    value: /^[0-9]+$/,
                                    message: 'Format harus berupa angka'
                                }
                            }}
                        />
                    </div>

                    {/* Section: Badan Hukum (opsional) */}
                    <div className="border-t pt-4 border-gray-100">
                        <FormSelect
                            label="Badan Hukum"
                            name="badanHukum"
                            options={BADAN_HUKUM_OPTIONS}
                            register={register}
                            errors={errors}
                            disabled={isSubmitting}
                            placeholder="Pilih badan hukum..."
                        />
                        {isYayasan && (
                            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                                <FormInput
                                    label="Nomor Akta Pendirian"
                                    name="nomorAkta"
                                    register={register}
                                    errors={errors}
                                    rules={{ maxLength: { value: 100, message: 'Maksimal 100 karakter' } }}
                                />
                                <FormInput
                                    label="Nama Notaris"
                                    name="notaris"
                                    register={register}
                                    errors={errors}
                                    rules={{ maxLength: { value: 150, message: 'Maksimal 150 karakter' } }}
                                />
                                <FormInput
                                    label="Nomor SK"
                                    name="nomorSk"
                                    register={register}
                                    errors={errors}
                                    rules={{ maxLength: { value: 100, message: 'Maksimal 100 karakter' } }}
                                />
                            </div>
                        )}
                    </div>

                    {/* Section: Lokasi */}
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 border-t pt-4 border-gray-100">
                        <FormSelect
                            label="Provinsi"
                            name="provinsi"
                            options={provinces}
                            register={register}
                            errors={errors}
                            disabled={isLoading || isSubmitting}
                            placeholder={isLoading ? "Memuat data..." : "Pilih Provinsi..."}
                            rules={{ required: 'Provinsi wajib dipilih' }}
                        />
                        <FormSelect
                            label="Kota / Kabupaten"
                            name="kota"
                            options={cities}
                            register={register}
                            errors={errors}
                            disabled={!selectedProvince || isLoading || isSubmitting}
                            placeholder={!selectedProvince ? "Pilih provinsi terlebih dahulu" : "Pilih Kota..."}
                            rules={{ required: 'Kota wajib dipilih' }}
                        />
                    </div>

                    {/* Section: Bidang Usaha (non-pendidikan) */}
                    <div className="border-t pt-4 border-gray-100">
                        <FormCheckboxGroup
                            label="Bidang Usaha (non-pendidikan)"
                            name="bidangUsaha"
                            options={businessTypes}
                            register={register}
                            errors={errors}
                            disabled={isSubmitting}
                            hint="Pilih satu atau lebih bidang usaha pesantren selain pendidikan."
                            rules={{
                                validate: (value) => (Array.isArray(value) && value.length > 0) || 'Pilih minimal satu bidang usaha'
                            }}
                        />
                        {lainnyaDipilih && (
                            <FormInput
                                label="Sebutkan bidang usaha lainnya"
                                name="bidangLainnya"
                                register={register}
                                errors={errors}
                                placeholder="Contoh: Perikanan"
                                rules={{ maxLength: { value: 200, message: 'Maksimal 200 karakter' } }}
                            />
                        )}
                    </div>

                    {/* Section: Unggah Dokumen */}
                    <div className="border-t pt-4 border-gray-100">
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <FormFileInput
                                label="Upload Logo"
                                name="logo"
                                accept=".jpeg,.jpg,.png,.webp"
                                hint="Format: JPEG, PNG, WEBP. Maks: 2MB."
                                register={register}
                                errors={errors}
                                rules={{
                                    required: 'Logo wajib diunggah',
                                    validate: {
                                        acceptedFormats: (files) => {
                                            if (!files || files.length === 0) return true;
                                            const type = files[0].type;
                                            return ['image/jpeg', 'image/png', 'image/webp'].includes(type) || 'Format harus JPEG, PNG, atau WEBP';
                                        },
                                        maxSize: (files) => {
                                            if (!files || files.length === 0) return true;
                                            return files[0].size <= 2 * 1024 * 1024 || 'Ukuran maksimal 2MB';
                                        }
                                    }
                                }}
                            />
                        </div>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <FormFileInput
                                label="Akta atau SK Badan Hukum"
                                name="aktaSk"
                                accept=".pdf"
                                hint="Format: PDF. Maks: 5MB."
                                register={register}
                                errors={errors}
                                rules={pdfRules}
                            />
                            <FormFileInput
                                label="Izin Kemenag, termasuk Nomor Statistik Pesantren"
                                name="izinKemenag"
                                accept=".pdf"
                                hint="Format: PDF. Maks: 5MB."
                                register={register}
                                errors={errors}
                                rules={pdfRules}
                            />
                        </div>
                    </div>

                    {/* Section: Kredensial */}
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 border-t pt-4 border-gray-100">
                        <FormInput
                            label="Email"
                            name="email"
                            type="email"
                            register={register}
                            errors={errors}
                            placeholder="email@pesantren.com"
                            rules={{
                                required: 'Email wajib diisi',
                                pattern: {
                                    value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                                    message: 'Format email tidak valid'
                                }
                            }}
                        />
                        <FormInput
                            label="Password"
                            name="password"
                            type="password"
                            register={register}
                            errors={errors}
                            placeholder="Minimal 8 karakter"
                            rules={{
                                required: 'Password wajib diisi',
                                minLength: {
                                    value: 8,
                                    message: 'Password minimal 8 karakter'
                                }
                            }}
                        />
                    </div>

                    {submitError && (
                        <div role="alert" className="bg-red-50 border-l-4 border-red-400 p-4 rounded-md">
                            <p className="text-sm font-medium text-red-700">Pendaftaran belum berhasil.</p>
                            <p className="text-sm text-red-700 mt-1">{submitError}</p>
                            <p className="text-sm text-red-700 mt-1">Periksa isian Anda, lalu tekan Daftar Sekarang untuk mencoba lagi.</p>
                        </div>
                    )}

                    <div className="pt-4 flex justify-end">
                        <button
                            type="submit"
                            disabled={isSubmitting}
                            className={`bg-blue-600 text-white font-medium py-2 px-6 rounded-md shadow focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-colors ${isSubmitting ? 'opacity-70 cursor-not-allowed' : 'hover:bg-blue-700'
                                }`}
                        >
                            {isSubmitting ? 'Memproses...' : 'Daftar Sekarang'}
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
};

export default PendaftaranBaru;
